import React, {useState, useMemo, useRef, useEffect} from 'react'
import all from '../tasty/all'

const ROW_HEIGHT = 60
const BUFFER_SIZE = 5

const Look = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [scrollTop, setScrollTop] = useState(0)
  const containerRef = useRef(null)

  // Filter videos based on search term - show ALL results
  const filteredVideos = useMemo(() => {
    if (!searchTerm.trim()) {
      return all
    }

    const term = searchTerm.toLowerCase()
    return all.filter(video => {
      const title = (video.title || '').toLowerCase()
      const description = (video.description || '').toLowerCase()
      return title.includes(term) || description.includes(term)
    })
  }, [searchTerm])

  // Calculate visible range
  const containerHeight = window.innerHeight - 180
  const totalHeight = filteredVideos.length * ROW_HEIGHT
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_SIZE)
  const endIndex = Math.min(
    filteredVideos.length - 1,
    Math.ceil((scrollTop + containerHeight) / ROW_HEIGHT) + BUFFER_SIZE
  )
  const visibleVideos = filteredVideos.slice(startIndex, endIndex + 1)

  // Reset scroll position when search changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
      setScrollTop(0)
    }
  }

  // Handle scroll
  const handleScroll = (e) => {
    setScrollTop(e.target.scrollTop)
  }

  // Copy filtered results to clipboard
  const copyToClipboard = () => {
    const text = filteredVideos.map(video => {
      return `${video.videoId}\n${video.title}\n${video.description}\n`
    }).join('\n')

    navigator.clipboard.writeText(text).then(() => {
      alert(`Copied ${filteredVideos.length} videos to clipboard!`)
    }).catch(err => {
      console.error('Failed to copy:', err)
      alert('Failed to copy to clipboard')
    })
  }

  return (
    <div style={local.pageContainer}>
      <div style={local.page}>
        <div style={local.header}>
          <h2>StarCraft Videos ({all.length} total)</h2>
          <input
            type="text"
            placeholder="Filter by title or description (e.g. 2010, Flash, TvZ)..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={local.searchInput}
          />
          <div style={local.statsRow}>
            <div style={local.stats}>
              Showing {filteredVideos.length} {searchTerm ? 'matching ' : ''}videos
            </div>
            {filteredVideos.length > 0 && (
              <button onClick={copyToClipboard} style={local.copyButton}>
                Copy {filteredVideos.length} to Clipboard
              </button>
            )}
          </div>
        </div>

        <div style={local.tableContainer}>
          {/* Header row */}
          <div style={local.headerRow}>
            <div style={local.headerCellVideoId}>Video ID</div>
            <div style={local.headerCellTitle}>Title</div>
            <div style={local.headerCellDescription}>Description</div>
          </div>

          {/* Virtualized list container */}
          <div
            ref={containerRef}
            onScroll={handleScroll}
            style={{
              height: containerHeight,
              overflow: 'auto',
              position: 'relative',
            }}
          >
            {/* Spacer for total height */}
            <div style={{height: totalHeight, position: 'relative'}}>
              {/* Visible rows */}
              <div style={{position: 'absolute', top: startIndex * ROW_HEIGHT, width: '100%'}}>
                {visibleVideos.map((video, i) => {
                  const index = startIndex + i
                  return (
                    <div
                      key={`${video.videoId}-${index}`}
                      style={{
                        height: ROW_HEIGHT,
                        display: 'flex',
                        borderBottom: '1px solid #e9ecef',
                      }}
                    >
                      <div style={local.cellVideoId}>
                        <a
                          href={`https://youtube.com/watch?v=${video.videoId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={local.link}
                        >
                          {video.videoId}
                        </a>
                      </div>
                      <div style={local.cellTitle}>{video.title}</div>
                      <div style={local.cellDescription}>{video.description}</div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const local = {
  pageContainer: {
    display: 'flex',
    width: '100vw',
    height: '100vh',
    overflow: 'hidden',
    padding: '20px',
    boxSizing: 'border-box',
  },
  page: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  header: {
    marginBottom: 20,
    flexShrink: 0,
  },
  searchInput: {
    width: '100%',
    padding: '12px 16px',
    fontSize: 16,
    border: '2px solid #ddd',
    borderRadius: 8,
    marginTop: 10,
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  statsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 10,
  },
  stats: {
    fontSize: 14,
    color: '#666',
  },
  copyButton: {
    backgroundColor: '#28a745',
    color: 'white',
    padding: '8px 16px',
    fontSize: 14,
    border: 'none',
    borderRadius: 5,
    cursor: 'pointer',
    fontWeight: 500,
  },
  tableContainer: {
    flex: 1,
    overflow: 'hidden',
    border: '1px solid #ddd',
    borderRadius: 8,
    backgroundColor: 'white',
  },
  headerRow: {
    display: 'flex',
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #dee2e6',
    fontWeight: 600,
    fontSize: 14,
  },
  headerCellVideoId: {
    padding: '12px 16px',
    width: '150px',
    flexShrink: 0,
  },
  headerCellTitle: {
    padding: '12px 16px',
    width: '300px',
    flexShrink: 0,
  },
  headerCellDescription: {
    padding: '12px 16px',
    flex: 1,
  },
  cellVideoId: {
    padding: '12px 16px',
    width: '150px',
    flexShrink: 0,
    fontFamily: 'monospace',
    fontSize: 14,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cellTitle: {
    padding: '12px 16px',
    width: '300px',
    flexShrink: 0,
    fontSize: 14,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cellDescription: {
    padding: '12px 16px',
    flex: 1,
    fontSize: 14,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflow: 'hidden',
  },
  link: {
    color: '#0066cc',
    textDecoration: 'none',
  },
}

export default Look
